import type { ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';

import type { ApiFilestackImageData } from '#/api/types/ApiFilestackImageData';
import { Box } from '#/components/Box/Box';
import { getAppConstants } from '#/config/appConstants';
import { useUser } from '#/contexts/UserEnvironmentContext';
import type {
  ApiImageData,
  OnBeforeUploadResult,
} from '#/hooks/useUpload/types';
import { useUpload } from '#/hooks/useUpload/useUpload';
import { useI18n } from '#/i18n/hooks/useI18n';
import { getUserType } from '#/utils/analytics/helpers/getUserType';
import { trackEvent } from '#/utils/analytics/track/trackEvent';
import {
  filestackSupportedMimeTypes,
  filestackSupportedMimeTypesWithoutSVG,
} from '#/utils/filestackSupportedMimetypes';

import {
  checkImageDimensions,
  runBeforeUploadSteps,
} from './beforeUploadSteps';
import { cropImage } from './cropImage';
import { ImagePickerDropzone } from './Dropzone/ImagePickerDropzone';
import { SVGConfirmationModal } from './SVGConfirmationModal';

type Props = {
  id?: string;
  name: string;
  onChange: (newImage: ApiFilestackImageData) => void;
  aspectRatio?: number;
  getDimensions?: boolean;
  disableCrop?: boolean;
  children: (renderProps: {
    onClick: () => void;
    dragActive: boolean;
    error: string | undefined;
    loading: boolean;
  }) => ReactNode;
  label?: string;
  extraAnalyticsData?: Record<string, unknown>;
  allowSVG?: boolean;
  showCropScreen?: boolean;
  qaId?: string;
  'data-qa-has-image'?: boolean;
};

export function ImagePickerInternal({
  id: idProp,
  name,
  onChange,
  aspectRatio,
  // TODO(@paprikka): remove these 2 props as they might not be needed any more
  // Context: https://idealist.slack.com/archives/CHJ9JK7TP/p1743005254772969
  // oxlint-disable-next-line no-unused-vars
  getDimensions,
  // oxlint-disable-next-line no-unused-vars
  disableCrop,
  children,
  label,
  extraAnalyticsData,
  allowSVG,
  showCropScreen,
  qaId,
  // NOTE: we're adding this only for backwards compatibility, but it's
  // meaningless to the user. Consider removing it and using a different
  // assertion.
  'data-qa-has-image': hasImage,
}: Props) {
  const { i18n } = useI18n();

  const { user } = useUser();
  const ref = useRef(null);

  const [showSVGConfirmationModal, setShowSVGConfirmationModal] =
    useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const svgResolveFunction = useRef<(() => void) | null>(null);
  const svgRejectFunction = useRef<(() => void) | null>(null);

  const id = idProp ?? `fs-picker-${name.replace(/\./g, '_')}`;
  const inputId = `${id}-input`;

  const checkSVGUploadConfirmedByUser = (
    file: File,
  ): Promise<OnBeforeUploadResult> => {
    const isSVG = file.type.includes('image/svg');
    if (!isSVG) return Promise.resolve({ type: 'continue', file });

    if (isSVG && !allowSVG)
      return Promise.resolve({
        type: 'error',
        error: new Error(i18n('SVG files are not allowed')),
      });

    return new Promise((resolve) => {
      // TODO: this is the last part of this component that is not agnostic of
      // Filestack. Find a way to remove it.
      if (getAppConstants().filestackMockEnabled) {
        // TODO(@paprikka): remove this once we have a real mock
        resolve({ type: 'continue', file });
        return;
      }

      setShowSVGConfirmationModal(true);
      svgResolveFunction.current = () => resolve({ type: 'continue', file });
      svgRejectFunction.current = () =>
        resolve({ type: 'cancel', reason: 'user_cancelled' });
    });
  };

  const maybeTrackUpload = useCallback(
    (file: ApiImageData) => {
      if (extraAnalyticsData?.skipTracking) return;

      const fileExtension = file?.filename?.split('.').pop();

      trackEvent('Success: Uploaded Image', {
        user_type: getUserType(user),
        file_extension: fileExtension,
        ...extraAnalyticsData,
      });
    },
    [user, extraAnalyticsData],
  );

  // TODO(@paprikka): create a story for this bug:
  // https://idealist.slack.com/archives/CHJ9JK7TP/p1742904479328999 In short:
  // the sizes and aspect ratios are not respected by the component.
  // This is an already existing regression.
  const { upload } = useUpload({
    onUploadComplete: (result) => {
      maybeTrackUpload(result);
      setLoading(false);
      onChange(result);
    },
    onUploadError: (uploadError) => {
      setError(uploadError.message);
      setLoading(false);
    },
    onUploadCancelled: () => setLoading(false),
    onBeforeUploadBegin: async (file) => {
      setLoading(true);

      // NOTE: We're using `runBeforeUploadSteps` to ensure that errors are
      // handled uniformly (and offload some of the work to the type system),
      // but `onBeforeUploadBegin` only expects a single function and is
      // agnostic of the concept of steps. If we end up having fewer checks here
      // it's 100% OK to drop it!
      return runBeforeUploadSteps(
        [
          checkSVGUploadConfirmedByUser,
          (f: File) =>
            showCropScreen
              ? cropImage(f, { aspectRatio })
              : Promise.resolve({ type: 'continue', file: f }),
          (f) => checkImageDimensions(i18n, f),
        ],
        file,
      );
    },
  });

  const handleDrop = async (osFile: File) => {
    setDragActive(false);
    await upload(osFile);
  };

  const handleDropError = (dzError: Error) => {
    setError(dzError.message);
    setLoading(false);
  };

  const activateFileInput = () => {
    const el = document.querySelector<HTMLElement>(`#${inputId}`);
    if (!el) throw new Error('Image input element not found');
    el.click();
  };

  return (
    <Box data-qa-has-image={hasImage} qaId={qaId} id={id} position="relative">
      {children({ onClick: activateFileInput, dragActive, error, loading })}

      <div ref={ref}>
        <label style={{ display: 'none' }} htmlFor={inputId}>
          {label}
        </label>

        <ImagePickerDropzone
          id={inputId}
          onDragEnter={() => setDragActive(true)}
          onDragLeave={() => setDragActive(false)}
          onPointerOver={() => setDragActive(true)}
          onPointerLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          onError={handleDropError}
          accept={
            allowSVG
              ? filestackSupportedMimeTypes
              : filestackSupportedMimeTypesWithoutSVG
          }
          maxSize={100 * 1024 * 1024}
        />
      </div>

      {showSVGConfirmationModal && (
        <SVGConfirmationModal
          onConfirm={() => {
            if (svgResolveFunction.current) {
              svgResolveFunction.current();
              setShowSVGConfirmationModal(false);
            }
          }}
          onClose={() => {
            if (svgRejectFunction.current) {
              svgRejectFunction.current();
              setShowSVGConfirmationModal(false);
            }
          }}
        />
      )}
    </Box>
  );
}
